/**
 * MapControls Component
 *
 * Basic map controls: Home, Zoom In, Zoom Out
 */

import { Plus, Minus, Layers, Map, TriangleAlert, RotateCw, X, Share2 } from 'lucide-react';
import { Cartesian3, Viewer as CesiumViewer } from 'cesium';
import { DEFAULT_CAMERA_POSITION } from '@/lib/cesium-config';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { useMission } from '@/features/mission/hooks/useMission';
import { toast } from 'sonner';


export const maps = [
  {
    id: '1',
    name: 'Peta Open Streetmap',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution:
      '&copy; Map tiles by <a href="http://stamen.com">Stamen Design</a>, <a href="http://creativecommons.org/licenses/by/3.0">CC BY 3.0</a> &mdash; Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
  {
    id: '2',
    name: 'Peta Gelap',
    url: 'https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png',
    attribution:
      '&copy; Tiles courtesy of the <a href="https://usgs.gov/">U.S. Geological Survey</a>',
  },
  {
    id: '3',
    name: 'Peta satelit',
    url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryTopo/MapServer/tile/{z}/{y}/{x}',
    attribution:
      '&copy; Tiles courtesy of the <a href="https://usgs.gov/">U.S. Geological Survey</a>',
  },
  {
    id: '4',
    name: 'Peta terang',
    url: 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSTopo/MapServer/tile/{z}/{y}/{x}',
    maxZoom: 20,
    attribution: 'Tiles courtesy of the <a href="https://usgs.gov/">U.S. Geological Survey</a>',
  },
];

export interface MapControlsProps {
  viewer: CesiumViewer | undefined;
  className?: string;
  selectedMapType?: string;
  onMapTypeChange?: (mapType: string) => void;
  showPoints?: boolean;
  hideAlert?: boolean;
  onPointsClick?: () => void;
  isSelectingPoints?: boolean;
}

export function MapControls({
  viewer,
  className,
  selectedMapType,
  onMapTypeChange,
  showPoints,
  hideAlert,
  onPointsClick: _onPointsClick,
  isSelectingPoints: _isSelectingPoints = false,
}: MapControlsProps) {
  const [isMapTypeOpen, setIsMapTypeOpen] = useState(false);

  const {
    isAddingWaypoint,
    setAddingWaypointMode,
  } = useMission();

  // State lokal hanya digunakan ketika komponen tidak dikontrol dari luar
  const [internalSelectedMapType, setInternalSelectedMapType] = useState('1');

  // Gunakan nilai dari props jika ada, jika tidak gunakan state internal
  const effectiveSelectedMapType = selectedMapType ?? internalSelectedMapType;

  const handleHomeClick = () => {
    if (!viewer) return;

    viewer.camera.flyTo({
      destination: Cartesian3.fromDegrees(
        DEFAULT_CAMERA_POSITION.longitude,
        DEFAULT_CAMERA_POSITION.latitude,
        DEFAULT_CAMERA_POSITION.height
      ),
      duration: 2,
    });

  };

  const handleZoomIn = () => {
    if (!viewer) return;
    viewer.camera.zoomIn(viewer.camera.positionCartographic.height * 0.5);
  };

  const handleZoomOut = () => {
    if (!viewer) return;
    viewer.camera.zoomOut(viewer.camera.positionCartographic.height * 0.5);
  };

  const handleMapType = () => {
    if (isMapTypeOpen) {
      setIsMapTypeOpen(false);
    } else {
      setIsMapTypeOpen(true);
    }
  };

  const handleMapTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    // Jika parent menyediakan handler, serahkan ke parent (controlled component)
    if (onMapTypeChange) {
      onMapTypeChange(value);
    } else {
      // Jika tidak ada handler eksternal, kelola state sendiri
      setInternalSelectedMapType(value);
    }
  };

  const handleCloseResetViewCard = () => {
    setIsMapTypeOpen(false);
  };

  const handleAddPoint = () => {
    setAddingWaypointMode(!isAddingWaypoint);
    if (!isAddingWaypoint) {
      toast.info('Click on the map to add a point', {
        duration: 3000,
      });
    }
  };

  return (
    <div
      className={cn(
        'absolute top-4 right-4 flex flex-col gap-4 z-10',
        className
      )}
    >
      <div className='bg-black/80 flex flex-col gap-1 p-1 rounded'>
        <button
          onClick={handleMapType}
          className="bg-transparent p-2 rounded-md flex items-center justify-center text-white"
          title="Map Type"
          aria-label="Choose map type"
        >
          <Map className="w-5 h-5 text-white" />
        </button>

        {!hideAlert && (
          <button
            // onClick={handleZoomIn}
            className="bg-transparent p-2 rounded-md flex items-center justify-center text-white"
            title="Alert"
            aria-label="Alert"
          >
            <TriangleAlert className="w-5 h-5 text-white" />
          </button>
        )}

        {showPoints && (
          <button
            onClick={handleAddPoint}
            className={`p-2 rounded-md flex items-center justify-center ${isAddingWaypoint
                ? 'bg-blue-600 text-white'
                : 'bg-transparent text-white hover:bg-white/10'
              }`}
            title={isAddingWaypoint ? 'Stop adding points' : 'Add points to map'}
            aria-label={isAddingWaypoint ? 'Stop adding points' : 'Add points to map'}
          >
            <Share2 className={`w-5 h-5 ${isAddingWaypoint ? 'text-white' : 'text-white'}`} />
          </button>
        )}

      </div>

      <div className='bg-black/80 flex flex-col gap-1 p-1 rounded'>
        <button
          onClick={handleZoomIn}
          className="bg-transparent p-2 rounded-md flex items-center justify-center text-white"
          title="Toggle Layers"
          aria-label="Toggle map layers"
        >
          <Plus className="w-5 h-5 text-white" />
        </button>

        {/* Settings Button */}
        <button
          onClick={handleHomeClick}
          className="bg-transparent p-2 rounded-md flex items-center justify-center text-white"
          title="Settings"
          aria-label="Open settings"
        >
          <RotateCw className="w-5 h-5 text-white" />
        </button>

        <button
          onClick={handleZoomOut}
          className="bg-transparent p-2 rounded-md flex items-center justify-center text-white"
          title="Toggle Layers"
          aria-label="Toggle map layers"
        >
          <Minus className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* 3. Card yang muncul secara kondisional */}
      {isMapTypeOpen && (
        <div
          className={cn(
            // Konversi dari LayerCard
            'absolute right-16',
            'w-64',
            'bg-black/85',
            'rounded-sm',
            'text-white',
            'z-20',
            'map-control-item'
          )}
        >
          {/* Konversi dari StyledHeaderCard */}
          <div className='flex items-center justify-between px-4 py-2.5 border-b border-white/30 font-bold text-base'>
            <div className='flex items-center gap-2'>
              <Layers size={18} />
              Tipe Peta
            </div>
            <button
              onClick={handleCloseResetViewCard}
              className='cursor-pointer text-lg text-white hover:text-red-400 transition-colors'
              title="Tutup"
            >
              <X size={20} />
            </button>
          </div>

          {/* Body Card */}
          <div className='p-4'>
            <div className='space-y-3'>
              {/* Item Pilihan 1 */}
              <div className='flex items-center gap-3'>
                <input
                  type="radio"
                  name="mapType"
                  id="mapType1"
                  value="1"
                  checked={effectiveSelectedMapType === '1'}
                  onChange={handleMapTypeChange}
                  className="
                    w-5 h-5 appearance-none cursor-pointer rounded-full border-2 border-white/50 bg-transparent
                    transition-all duration-200 ease-in-out
                    checked:border-white checked:bg-white
                    checked:shadow-[inset_0_0_0_4px_#fff,inset_0_0_0_8px_#000]
                    focus:outline-none focus:ring-2 focus:ring-white/50
                  "
                />
                <label
                  htmlFor="mapType1"
                  // Konversi dari .form-check-label
                  className="text-sm text-white flex items-center cursor-pointer hover:text-white/80"
                >
                  Open Street Map
                </label>
              </div>

              {/* Item Pilihan 2 */}
              <div className='flex items-center gap-3'>
                <input
                  type="radio"
                  name="mapType"
                  id="mapType2"
                  value="2"
                  checked={effectiveSelectedMapType === '2'}
                  onChange={handleMapTypeChange}
                  className="
                    w-5 h-5 appearance-none cursor-pointer rounded-full border-2 border-white/50 bg-transparent
                    transition-all duration-200 ease-in-out
                    checked:border-white checked:bg-white
                    checked:shadow-[inset_0_0_0_4px_#fff,inset_0_0_0_8px_#000]
                    focus:outline-none focus:ring-2 focus:ring-white/50
                  "
                />
                <label
                  htmlFor="mapType2"
                  className="text-sm text-white flex items-center cursor-pointer hover:text-white/80"
                >
                  Peta Gelap
                </label>
              </div>

              {/* Item Pilihan 3 */}
              <div className='flex items-center gap-3'>
                <input
                  type="radio"
                  name="mapType"
                  id="mapType3"
                  value="3"
                  checked={effectiveSelectedMapType === '3'}
                  onChange={handleMapTypeChange}
                  className="
                    w-5 h-5 appearance-none cursor-pointer rounded-full border-2 border-white/50 bg-transparent
                    transition-all duration-200 ease-in-out
                    checked:border-white checked:bg-white
                    checked:shadow-[inset_0_0_0_4px_#fff,inset_0_0_0_8px_#000]
                    focus:outline-none focus:ring-2 focus:ring-white/50
                  "
                />
                <label
                  htmlFor="mapType3"
                  className="text-sm text-white flex items-center cursor-pointer hover:text-white/80"
                >
                  Peta Satelit
                </label>
              </div>

              {/* Item Pilihan 4 */}
              <div className='flex items-center gap-3'>
                <input
                  type="radio"
                  name="mapType"
                  id="mapType4"
                  value="4"
                  checked={effectiveSelectedMapType === '4'}
                  onChange={handleMapTypeChange}
                  className="
                    w-5 h-5 appearance-none cursor-pointer rounded-full border-2 border-white/50 bg-transparent
                    transition-all duration-200 ease-in-out
                    checked:border-white checked:bg-white
                    checked:shadow-[inset_0_0_0_4px_#fff,inset_0_0_0_8px_#000]
                    focus:outline-none focus:ring-2 focus:ring-white/50
                  "
                />
                <label
                  htmlFor="mapType4"
                  className="text-sm text-white flex items-center cursor-pointer hover:text-white/80"
                >
                  Peta Terang
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
